import { Fragment, useEffect, useId, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { CaretRightIcon as CaretRight, CopyIcon as Copy } from "@phosphor-icons/react";
import { namesText } from "@/lib/objectContext";
import { COPY_FAILURE_MESSAGE, COPY_SUCCESS_MESSAGE, copyCompareSummary } from "@/lib/compareSummaryText";

// Iter 61/62/64/65 — compact comparison summary under the compare note. Counts, the names behind them and
// their evidence links come from the structured object_context.compare_context — never from the answer text.
const slug = (s) => String(s).toLowerCase().replace(/[^a-z0-9]+/g, "-");
const LINK_CLASS =
  "text-[var(--ml-primary)] underline decoration-dotted underline-offset-2 hover:decoration-solid break-words focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ml-primary)] rounded-sm";

/** One drawer name: a workspace-bound evidence link when the destination is safe, otherwise plain text. */
function DrawerName({ item, fallback }) {
  const label = (item && item.label) || fallback;
  if (item && item.to) {
    return (
      <Link
        to={item.to}
        className={LINK_CLASS}
        aria-label={`${label} — open ${item.destination}`}
        title={`Open ${item.destination}`}
        data-testid="compare-drawer-link"
        data-kind={item.kind}
      >
        {label}
      </Link>
    );
  }
  return <span data-testid="compare-drawer-text" data-kind={item ? item.kind : "text"}>{label}</span>;
}

function InlineNames({ c }) {
  if (!c.names.length) return <span className="min-w-0 break-words text-[var(--ml-text-primary)]">{namesText(c)}</span>;
  const items = Array.isArray(c.items) ? c.items : [];
  return (
    <span className="min-w-0 break-words text-[var(--ml-text-primary)]">
      {c.names.map((n, i) => (
        <Fragment key={`${n}-${i}`}>
          {i > 0 && ", "}
          <DrawerName item={items[i]} fallback={n} />
        </Fragment>
      ))}
      {c.more > 0 && `, +${c.more} more`}
    </span>
  );
}

function NameLines({ row }) {
  const named = row.cells.filter((c) => Array.isArray(c.names));
  if (!named.length) return null;
  if (named.every((c) => !c.names.length && !c.more)) {
    return (
      <div className="text-[var(--ml-text-secondary)] break-words" data-testid={`compare-summary-names-${row.key}-empty`}>
        No names recorded on either side.
      </div>
    );
  }
  return named.map((c) => (
    <div key={c.k} className="flex flex-wrap gap-x-1 min-w-0 break-words" data-testid={`compare-summary-names-${row.key}-${slug(c.k)}`}>
      <span className="text-[var(--ml-text-secondary)] shrink-0">{c.k}:</span>
      {c.layout === "list" && c.names.length ? (
        <ul className="m-0 p-0 list-none min-w-0 break-words w-full">
          {c.names.map((n, i) => (
            <li key={`${n}-${i}`} className="break-words">
              <DrawerName item={Array.isArray(c.items) ? c.items[i] : null} fallback={n} />
            </li>
          ))}
          {c.more > 0 && <li>+{c.more} more</li>}
        </ul>
      ) : (
        <InlineNames c={c} />
      )}
    </div>
  ));
}

function CellValue({ c }) {
  if (c.link) {
    return (
      <Link to={c.link.to} className={LINK_CLASS} aria-label={`${c.v} — open ${c.link.destination}`} title={`Open ${c.link.destination}`}
        data-testid="compare-drawer-link" data-kind="disposition">
        {c.v}
      </Link>
    );
  }
  return c.v;
}

function DetailRow({ row }) {
  return (
    <div
      className="flex flex-wrap gap-x-4 gap-y-0.5 py-1.5 border-b border-[var(--ml-border)] last:border-b-0 min-w-0"
      data-testid={`compare-summary-row-${row.key}`}
    >
      <span className="w-full sm:w-40 shrink-0 text-[10px] uppercase tracking-wider text-[var(--ml-text-secondary)]">{row.label}</span>
      <div className="flex-1 min-w-0 space-y-0.5">
        <div className="flex flex-wrap gap-x-4 gap-y-0.5 min-w-0">
          {row.cells.map((c) => (
            <span key={c.k} className="min-w-0 break-words">
              <span className="text-[var(--ml-text-secondary)]">{c.k}</span> <CellValue c={c} />
            </span>
          ))}
        </div>
        <NameLines row={row} />
      </div>
    </div>
  );
}

// Iter 66 — impact graph delta (shared / left-only / right-only blast radius) under the main rows.
function ImpactOverlap({ impact }) {
  if (!impact) return null;
  return (
    <div className="mt-2 pt-2 border-t border-[var(--ml-border)] min-w-0" data-testid="compare-impact-section">
      <div className="text-[10px] uppercase tracking-wider text-[var(--ml-text-primary)] font-semibold break-words">{impact.header}</div>
      {impact.used ? (
        <>
          <div className="text-[var(--ml-text-secondary)] break-words" data-testid="compare-impact-totals">
            {impact.totals}
            {impact.larger && (
              <>
                {" · "}
                <span data-testid="compare-impact-larger">Larger blast radius: {impact.larger}</span>
                <span> (by node count — size alone is not risk)</span>
              </>
            )}
          </div>
          {impact.rows.map((row) => <DetailRow key={row.key} row={row} />)}
        </>
      ) : (
        <div className="text-[var(--ml-text-secondary)] break-words" data-testid="compare-impact-unavailable">
          Not available: {impact.unavailable}
        </div>
      )}
      {impact.used && impact.warnings.length > 0 && (
        <ul className="m-0 p-0 list-none text-[#a2191f] break-words" data-testid="compare-impact-warnings">
          {impact.warnings.map((w) => (<li key={w}>{w}</li>))}
        </ul>
      )}
    </div>
  );
}

export function CompareSummaryPanel({ summary, objectContext, createdAt }) {
  const [open, setOpen] = useState(false);
  const [copyStatus, setCopyStatus] = useState(null); // Iter 64 — { ok, message }
  const timerRef = useRef(null);
  const detailsId = `${useId()}-details`;
  useEffect(() => () => { if (timerRef.current) window.clearTimeout(timerRef.current); }, []);
  if (!summary) return null;

  const handleCopy = async () => {
    const { ok } = await copyCompareSummary(objectContext, { generatedAt: createdAt });
    setCopyStatus({ ok, message: ok ? COPY_SUCCESS_MESSAGE : COPY_FAILURE_MESSAGE });
    if (timerRef.current) window.clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(() => setCopyStatus(null), ok ? 3000 : 8000);
  };

  return (
    <section
      className="mb-3 max-w-full border border-[var(--ml-border)] bg-[var(--ml-surface)]"
      aria-label={`Comparison summary${summary.label ? `: ${summary.label}` : ""}`}
      data-testid="compare-summary-panel"
    >
      <div className="flex flex-wrap items-center gap-x-2 min-w-0">
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          aria-expanded={open}
          aria-controls={detailsId}
          aria-label={open ? "Hide comparison details" : "Show comparison details"}
          className="ml-mono text-[10px] uppercase tracking-widest flex-1 min-w-0 text-left inline-flex items-center gap-1.5 px-3 py-2 text-[var(--ml-text-primary)] hover:text-[var(--ml-primary)] focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--ml-primary)] transition-colors"
          data-testid="compare-summary-toggle"
        >
          <CaretRight size={11} weight="bold" aria-hidden="true" className={`shrink-0 transition-transform ${open ? "rotate-90" : ""}`} />
          Comparison summary
        </button>
        {objectContext && (
          <button
            type="button"
            onClick={handleCopy}
            aria-label="Copy comparison summary as plain text"
            title="Copy the comparison summary and object names as plain text"
            className="ml-mono text-[10px] uppercase tracking-widest shrink-0 inline-flex items-center gap-1 mr-2 px-2 py-1 border border-[var(--ml-border)] text-[var(--ml-text-secondary)] hover:text-[var(--ml-primary)] hover:border-[var(--ml-primary)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ml-primary)] transition-colors"
            data-testid="compare-copy-button"
          >
            <Copy size={11} weight="bold" aria-hidden="true" />
            Copy summary
          </button>
        )}
      </div>
      <div role="status" aria-live="polite" className="px-3 min-w-0">
        {copyStatus && (
          <span
            className={`block ml-mono text-[10px] pb-1 break-words ${copyStatus.ok ? "text-[#0e6027]" : "text-[#a2191f]"}`}
            data-testid="compare-copy-status"
            data-ok={copyStatus.ok ? "true" : "false"}
          >
            {copyStatus.message}
          </span>
        )}
      </div>
      <dl className="ml-mono text-[11px] px-3 pb-2 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 min-w-0" data-testid="compare-summary-compact">
        {summary.compact.map((line) => (
          <Fragment key={line.key}>
            <dt className="text-[var(--ml-text-secondary)] whitespace-nowrap">{line.label}:</dt>
            <dd className="min-w-0 break-words m-0" data-testid={`compare-summary-${line.key}`}>{line.value}</dd>
          </Fragment>
        ))}
      </dl>
      {open && (
        <div
          id={detailsId}
          role="region"
          aria-label="Comparison details"
          className="ml-mono text-[11px] border-t border-[var(--ml-border)] px-3 py-2"
          data-testid="compare-summary-details"
        >
          {summary.rows.map((row) => <DetailRow key={row.key} row={row} />)}
          <ImpactOverlap impact={summary.impact} />
        </div>
      )}
    </section>
  );
}

export default CompareSummaryPanel;
